import axios from 'axios';
import { env } from '../../config/env';
import { tokenService } from '../auth/token.service';
import { oauth2Service } from '../auth/oauth2.service';

if (!env.api.PRODUCT_BACKEND_URL) {
    throw new Error('VITE_PRODUCT_BACKEND_URL is not configured. Set it in your .env file.');
}

const baseURL = `${env.api.PRODUCT_BACKEND_URL.replace(/\/+$/, '')}`;

export const workerClient = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

workerClient.interceptors.request.use(
    (config) => {
        const token = tokenService.getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

let refreshPromise: Promise<string> | null = null;

const isAuthRoute = () =>
    window.location.pathname.startsWith('/oauth2/callback') ||
    window.location.pathname.startsWith('/oauth-callback') ||
    window.location.pathname.startsWith('/login');

workerClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            const refreshToken = tokenService.getRefreshToken();
            if (!refreshToken) {
                tokenService.clear();
                if (!isAuthRoute()) window.location.href = '/login';
                return Promise.reject(error);
            }

            try {
                if (!refreshPromise) {
                    refreshPromise = oauth2Service.refreshAccessToken();
                }
                const newAccessToken = await refreshPromise;
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return workerClient(originalRequest);
            } catch (refreshError) {
                tokenService.clear();
                if (!isAuthRoute()) window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                refreshPromise = null;
            }
        }

        return Promise.reject(error);
    }
);

export default workerClient;
